import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit, Trash2, X, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/helpers';

const AdminPaymentList = () => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        content: '',
        isActive: true,
        imageFile: null, 
        existingImageUrl: '' 
    });

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 
    const [jumpPage, setJumpPage] = useState('');

    const fetchMethods = async () => {
        setLoading(true);
        try {
            const res = await adminStaffService.getAllPaymentMethods();
            const data = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            setPaymentMethods(data);
        } catch (error) {
            console.error("Lỗi lấy danh sách PT thanh toán:", error);
            toast.error("Không thể tải danh sách phương thức thanh toán!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMethods();
    }, []);

    // --- Pagination Logic ---
    const totalPages = Math.ceil(paymentMethods.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(currentPage, totalPages);
    const paginatedMethods = paymentMethods.slice((validCurrentPage - 1) * itemsPerPage, validCurrentPage * itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleJumpPage = () => {
        const page = parseInt(jumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpPage('');
        } else {
            setJumpPage('');
        }
    };

    const handleOpenModal = (method = null) => {
        if (method) {
            setEditingMethod(method);
            setFormData({
                name: method.name || '',
                content: method.content || '',
                isActive: method.isActive !== undefined ? method.isActive : true,
                imageFile: null,
                existingImageUrl: method.imageUrl || ''
            });
        } else {
            setEditingMethod(null);
            setFormData({ name: '', content: '', isActive: true, imageFile: null, existingImageUrl: '' });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingMethod(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setFormData(prev => ({ ...prev, imageFile: files[0] }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.warning("Vui lòng nhập tên phương thức!");
            return;
        }

        setIsSaving(true);
        try {
            const submitData = new FormData();
            
            const dto = {
                name: formData.name.trim(),
                content: formData.content.trim(),
                isActive: formData.isActive
            };
            
            submitData.append('data', JSON.stringify(dto));
            
            if (formData.imageFile) {
                submitData.append('image', formData.imageFile);
            }

            if (editingMethod) {
                await adminStaffService.updatePaymentMethod(editingMethod.id, submitData);
                toast.success("Cập nhật phương thức thành công!");
            } else {
                await adminStaffService.createPaymentMethod(submitData);
                toast.success("Thêm phương thức thành công!");
                setCurrentPage(1); // Chuyển về trang đầu khi thêm mới
            }
            handleCloseModal();
            fetchMethods();
        } catch (error) {
            toast.error(editingMethod ? "Lỗi khi cập nhật!" : "Lỗi khi thêm mới!");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa phương thức thanh toán này?")) {
            try {
                await adminStaffService.deletePaymentMethod(id);
                toast.success("Xóa phương thức thành công!");
                fetchMethods();
            } catch (error) {
                toast.error("Lỗi khi xóa phương thức!");
            }
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <CreditCard size={26} className="text-tnc-orange" /> Quản lý PT Thanh Toán
                </h2>
                <button 
                    onClick={() => handleOpenModal()} 
                    className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-4 py-2 rounded-md font-bold flex items-center gap-2 shadow-sm transition-colors"
                >
                    <Plus size={18} /> Thêm phương thức
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                        <thead>
                            <tr className="bg-[#002855] text-white text-sm">
                                <th className="px-6 py-4 font-medium w-16">Ảnh/QR</th>
                                <th className="px-6 py-4 font-medium">Tên Phương Thức</th>
                                <th className="px-6 py-4 font-medium min-w-[250px]">Nội dung (Hướng dẫn)</th>
                                <th className="px-6 py-4 font-medium text-center">Trạng Thái</th>
                                <th className="px-6 py-4 font-medium text-right">Thao Tác</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-16 text-gray-500 font-medium">Đang tải dữ liệu...</td></tr>
                            ) : paymentMethods.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-16 text-gray-500 font-medium">Chưa có phương thức thanh toán nào!</td></tr>
                            ) : (
                                paginatedMethods.map((method) => (
                                    <tr key={method.id} className="border-b hover:bg-orange-50/30 transition-colors">
                                        <td className="px-6 py-4">
                                            {method.imageUrl ? (
                                                <img src={getImageUrl(method.imageUrl)} alt="QR/Icon" className="w-12 h-12 object-contain rounded border border-gray-200 bg-white" />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 border border-gray-200">
                                                    <CreditCard size={20} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-bold text-gray-800">{method.name}</td>
                                        <td className="px-6 py-4 text-gray-500 max-w-xs truncate" title={method.content}>
                                            {method.content || '—'}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${method.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {method.isActive ? 'Đang hoạt động' : 'Tạm ẩn'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 flex justify-end items-center gap-2 mt-2">
                                            <button 
                                                onClick={() => handleOpenModal(method)} 
                                                className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 p-1.5 rounded transition-colors" 
                                                title="Sửa"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(method.id)} 
                                                className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-1.5 rounded transition-colors" 
                                                title="Xóa"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- Phân trang Payment --- */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-wrap justify-center items-center gap-4 p-4 border-t border-gray-200 bg-white">
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => handlePageChange(validCurrentPage - 1)} 
                                disabled={validCurrentPage === 1}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                                {[...Array(totalPages)].map((_, i) => {
                                    const page = i + 1;
                                    return (
                                        <button 
                                            key={page} 
                                            onClick={() => handlePageChange(page)}
                                            className={`w-8 h-8 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                ${validCurrentPage === page 
                                                    ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    )
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(validCurrentPage + 1)} 
                                disabled={validCurrentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 border-l border-gray-300 pl-4">
                            <span className="text-sm text-gray-600 font-medium">Đến trang:</span>
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                value={jumpPage}
                                onChange={(e) => setJumpPage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleJumpPage()}
                                placeholder={validCurrentPage.toString()}
                                className="w-14 h-8 px-2 text-center border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 shadow-sm"
                            />
                            <button
                                onClick={handleJumpPage}
                                className="px-3 h-8 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors shadow-sm"
                            >
                                Đi
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Thêm/Sửa */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden relative">
                        <div className="bg-[#002855] p-4 text-white flex justify-between items-center">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <CreditCard size={20} className="text-tnc-orange" /> 
                                {editingMethod ? 'Sửa PT thanh toán' : 'Thêm PT thanh toán mới'}
                            </h3>
                            <button onClick={handleCloseModal} className="hover:bg-white/20 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Tên phương thức <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" 
                                    name="name"
                                    placeholder="VD: Chuyển khoản Vietcombank"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange transition-colors"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Ảnh Icon / Mã QR Ngân hàng</label>
                                <div className="flex items-center gap-4">
                                    {(formData.imageFile || formData.existingImageUrl) ? (
                                        <img 
                                            src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : getImageUrl(formData.existingImageUrl)} 
                                            alt="Preview" 
                                            className="w-16 h-16 object-contain rounded border border-gray-200 bg-gray-50" 
                                        />
                                    ) : (
                                        <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded bg-gray-50 flex items-center justify-center text-gray-400">
                                            <ImageIcon size={24} />
                                        </div>
                                    )}
                                    <input 
                                        type="file" 
                                        name="imageFile"
                                        accept="image/*"
                                        onChange={handleInputChange}
                                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-orange-50 file:text-tnc-orange hover:file:bg-orange-100 cursor-pointer"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Nội dung hướng dẫn</label>
                                <textarea 
                                    name="content"
                                    placeholder="VD: STK 123456789 - DO THANH NAM. Cú pháp chuyển tiền: Mã Đơn Hàng"
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    rows="4"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange transition-colors resize-none"
                                ></textarea>
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mt-2">
                                <input 
                                    type="checkbox" 
                                    name="isActive"
                                    checked={formData.isActive} 
                                    onChange={handleInputChange} 
                                    className="w-5 h-5 text-tnc-orange rounded cursor-pointer" 
                                />
                                <div>
                                    <span className="font-bold text-gray-800 block text-sm">Cho phép hoạt động</span>
                                    <span className="text-xs text-gray-500">Khách hàng có thể chọn phương thức này.</span>
                                </div>
                            </label>

                            <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button" 
                                    onClick={handleCloseModal}
                                    className="px-4 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-lg hover:bg-gray-200 transition-colors w-1/3"
                                >
                                    Hủy
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isSaving}
                                    className="flex-1 bg-tnc-orange text-white font-bold py-2.5 rounded-lg hover:bg-tnc-orange-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? "Đang lưu..." : "Lưu thay đổi"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPaymentList;